// Contenu de la plaquette — Onglet "Services"
// Sections H à N

// ---------------------------------------------------------------------------
// Catégories de services
// ---------------------------------------------------------------------------
// Utilisées à la fois par les onglets de la page /services et par le sous-menu
// "Services" de la barre de navigation : chaque id sert d'ancre
// (/services#individuel, /services#couple, ...).
export const serviceCategories = [
  { id: "individuel", label: "Individuel" },
  { id: "couple", label: "Couple" },
  { id: "groupes", label: "Interventions de groupes" },
  { id: "organismes", label: "Organismes / Entreprises" },
  { id: "prises-de-parole", label: "Prises de parole" },
];

// ---------------------------------------------------------------------------
// Aperçu des services (page d'accueil)
// ---------------------------------------------------------------------------
// Offres de la plaquette, avec leur accroche (citation de la plaquette),
// durée et tarif. Chaque carte renvoie vers l'onglet correspondant de
// /services et propose la réservation du service.
export const homeServices = [
  {
    id: "therapie-intersectionnelle",
    category: "individuel",
    title: "La thérapie intersectionnelle",
    description:
      "Un voyage intérieur afin d'explorer, ressentir et réconcilier les différentes parts de vous-même.",
    duration: "1h30 (psychothérapie intégrative + Focus-Action)",
    price: "150 € puis 130 €",
    icon: "/icon-person.png",
  },
  {
    id: "focus-action",
    category: "individuel",
    title: "Le suivi Focus-Action",
    description:
      "Un cap, une boussole vers un objectif clair et une transformation sereine.",
    duration: "45 min ou 1h15",
    price: "À partir de 100 €",
    icon: "/icon-boat.png",
  },
  {
    id: "accompagnement-intersectionnel",
    category: "individuel",
    title: "L'accompagnement intersectionnel",
    description:
      "Un accompagnement holistique de 3h par mois pour traverser une transformation, une situation de vie complexe ou un sentiment de détresse.",
    duration: "3h par mois, sur 1, 3 ou 6 mois",
    price: "320 €/mois, dégressif",
    icon: "/icon-clock.png",
  },
  {
    id: "therapie-couple",
    category: "couple",
    title: "La thérapie de couple",
    description: "Passez du « Je » et « Tu » au « Nous » !",
    duration: "1h30",
    price: "130 €",
    icon: "/icon-handshake.png",
  },
  {
    id: "groupe-de-parole",
    category: "groupes",
    title: "Le groupe de parole intersectionnel",
    description: "Transformer la douleur en force collective !",
    duration: "2h par mois, 6 à 8 participant·e·s",
    price: "Sur demande",
    icon: "/icon-handshake.png",
  },
  {
    id: "organismes-prises-de-parole",
    category: "organismes",
    title: "Organismes & prises de parole",
    description:
      "Formation, conseil et accompagnement du vivre-ensemble interculturel, interventions publiques et éducation populaire.",
    duration: "Sur mesure",
    price: "Sur devis",
    icon: "/icon-globe.png",
  },
];

// ---------------------------------------------------------------------------
// Services réservables
// ---------------------------------------------------------------------------
// Liste proposée à l'étape 1 de la page /rendez-vous. Les boutons
// "Réserver ce service" des cartes renvoient vers /rendez-vous?service=<id>
// pour présélectionner le service correspondant.
// `durationMinutes` fixe la durée des créneaux proposés pour ce service ;
// sans cette clé (formules mensuelles, sur mesure), on utilise la durée de
// séance par défaut de l'agenda (50 min, voir google-apps-script/Code.gs).
export const bookableServices = [
  {
    id: "therapie-intersectionnelle",
    category: "individuel",
    title: "La thérapie intersectionnelle",
    duration: "1h30",
    durationMinutes: 90,
    price: "150 € la 1re séance, puis 130 €",
  },
  {
    id: "focus-action-45",
    category: "individuel",
    title: "Le suivi Focus-Action — 45 min",
    duration: "45 min",
    durationMinutes: 45,
    price: "100 € la 1re séance, puis 80 €",
  },
  {
    id: "focus-action-75",
    category: "individuel",
    title: "Le suivi Focus-Action — 1h15",
    duration: "1h15",
    durationMinutes: 75,
    price: "120 € la 1re séance, puis 100 €",
  },
  {
    id: "accompagnement-intersectionnel",
    category: "individuel",
    title: "L'accompagnement intersectionnel",
    duration: "3h par mois, sur 1, 3 ou 6 mois",
    price: "À partir de 320 € / mois (dégressif)",
  },
  {
    id: "therapie-couple",
    category: "couple",
    title: "La thérapie de couple",
    duration: "1h30",
    durationMinutes: 90,
    price: "130 €",
  },
  {
    id: "groupe-de-parole",
    category: "groupes",
    title: "Le groupe de parole intersectionnel",
    duration: "2h, une fois par mois",
    price: "Sur demande",
  },
  {
    id: "intervention-organisme",
    category: "organismes",
    title: "Intervention en organisme / entreprise",
    duration: "Sur mesure",
    price: "Sur devis",
  },
  {
    id: "prise-de-parole",
    category: "prises-de-parole",
    title: "Prise de parole / Éducation populaire",
    duration: "Sur mesure",
    price: "Sur devis",
  },
];

// ---------------------------------------------------------------------------
// a) Individuel
// ---------------------------------------------------------------------------

// Section H — introduction
export const individualIntro = {
  banner: {
    src: "/mains-soutien.jpg",
    alt: "Deux mains jointes en signe de soutien",
  },
  heading: "Mon suivi individuel",
  text: "Explorez nos options et trouvez le soutien individuel qu'il vous faut !",
  note:
    "La majorité de ces services individuels sont déclinables en version visio / hybride. À voir ensemble au cas par cas, quelles pertinence et possibilités.",
};

// Section I — Formule 1
export const formula1 = {
  id: "formule-1",
  title: "La thérapie intersectionnelle",
  summary: "1h30, incluant 45 min de psychothérapie intégrative et 45 min de Focus-Action.",
  price: "150 € la 1re séance, 130 € ensuite (120 € patients déjà suivis)",
  bookingOptions: [{ serviceId: "therapie-intersectionnelle", label: "Réserver ce service" }],
  intro:
    "Le suivi s'effectue sur une base régulière avec un écart de 4 à 5 semaines maximum, pour permettre une certaine stabilité et cohérence thérapeutique.",
  details: [
    "Durée : 1h30, incluant 45 min de psychothérapie intégrative et 45 min de Focus-Action.",
    "Prix : 150 € la première séance et 130 € les séances suivantes (120 € pour les patient·e·s déjà suivi·e·s).",
    "Une facture vous sera remise dans la semaine suivant la séance pour un éventuel remboursement par votre mutuelle. Veuillez contacter votre mutuelle pour connaître ses conditions.",
  ],
  note:
    "Note : pour les patient·e·s ayant déjà été suivi·e·s en psychothérapie par la thérapeute, il est possible d'étendre le délai à 1 mois, 2 mois, 3 mois ou sur demande ponctuelle en cas de besoin. Le travail de psychothérapie intégrative ayant déjà été exploré ensemble, le voyage se transforme en « visite intérieure », s'appuie sur une relation de confiance déjà solide et ne met pas en péril l'équilibre du (de la) patient(e).",
  quote: "Un voyage intérieur afin d'explorer, ressentir et réconcilier les différentes parts de vous-même.",
};

// Section J — Formule 2
// NB : le prix affiché pour l'option 1 (45 min) a été harmonisé avec le
// tableau comparatif (section L) — voir le point signalé en fin de mission :
// le texte original de la plaquette indiquait 70 € pour les séances
// suivantes, alors que le tableau distingue 80 € (nouveaux patients) et 70 €
// (patients déjà suivis). C'est le tableau (section L) qui fait foi.
export const formula2 = {
  id: "formule-2",
  title: "Le suivi Focus-Action",
  summary: "Deux options selon votre besoin : 45 min ou 1h15.",
  price: "À partir de 100 € la première séance (voir tableau comparatif)",
  bookingOptions: [
    { serviceId: "focus-action-45", label: "Réserver — 45 min" },
    { serviceId: "focus-action-75", label: "Réserver — 1h15" },
  ],
  intro:
    "Lorsqu'un travail psychothérapeutique intégratif a déjà été réalisé en amont ou en parallèle, un suivi Focus-Action peut être envisagé. Les objectifs à court et moyen terme sont alors identifiés, et un programme de méthodes et d'outils est défini dès la première séance afin de fixer des axes de travail clairs, cohérents et favorables à de meilleurs résultats comme à un meilleur confort thérapeutique.",
  frequency:
    "La fréquence varie selon les objectifs et la situation du (de la) patient(e) : une séance toutes les 1 à 2 semaines au début, puis toutes les 3 à 4 semaines lorsque le suivi est bien engagé et que le/la patient(e) gagne en autonomie.",
  optionsHeading: "Deux possibilités s'offrent à vous selon votre besoin :",
  options: [
    "Option 1 — 45 min : 100 € la première séance, puis 80 € les séances suivantes pour les nouveaux patients (70 € pour les patient·e·s déjà suivi·e·s — voir tableau comparatif ci-dessous).",
    "Option 2 — 1h15 : 120 € la première séance et 100 € les suivantes.",
  ],
  details: [
    "Une facture vous sera remise dans la semaine suivant la séance pour un éventuel remboursement par votre mutuelle. Veuillez contacter votre mutuelle pour connaître ses conditions.",
  ],
  quote: "Un cap, une boussole vers un objectif clair et une transformation sereine.",
};

// Section K — Formule 3
export const formula3 = {
  id: "formule-3",
  title: "L'accompagnement intersectionnel",
  summary: "Un accompagnement holistique de 3h par mois, sur 1, 3 ou 6 mois.",
  price: "À partir de 320 € / mois (forfait dégressif)",
  bookingOptions: [{ serviceId: "accompagnement-intersectionnel", label: "Réserver ce service" }],
  image: {
    src: "/illustration-cerveau-tricot.jpg",
    alt: "Illustration de mains tricotant un cerveau",
  },
  intro: "Ce type d'accompagnement est adapté pour vous dans les cas de figure suivants :",
  cases: [
    {
      title: "Un parcours d'action ou de transformation orienté résultats",
      text:
        "Vous êtes dans un parcours d'action ou de transformation personnelle / professionnelle orienté résultats, avec des objectifs identifiés. Vous êtes plus ou moins conscient·e des défis qui vous attendent et cette démarche nécessite un plan d'action pertinent et flexible, pour vous aider à garder le cap, prioriser les points clés, hiérarchiser les étapes et identifier des leviers d'action au fur et à mesure du processus. Exemples : passage d'un concours, d'une épreuve compétitive, prise de poste à responsabilités, création d'une entreprise, transition de genre, coming-out, parcours PMA, processus de création artistique…",
    },
    {
      title: "Une situation de vie complexe ou un évènement isolé exceptionnel",
      text:
        "Vous traversez une situation de vie complexe ou avez été confronté·e à un évènement isolé exceptionnel qui vous a fait perdre vos repères, menace votre équilibre psychique dans le présent et vous empêche de vous projeter dans l'avenir de manière sereine. Vous souhaitez sortir de cette impasse et surmonter cette épreuve en agissant à votre rythme sur une période donnée, avec la possibilité de mobiliser différents types de ressources (internes et externes) selon les enjeux que vous rencontrerez au fur et à mesure. Exemples : deuil familial, TS, annonce d'un diagnostic médical, attente d'un verdict juridique, séparation, accident, agression.",
    },
    {
      title: "Un sentiment de détresse en tant que personne intersectionnelle",
      text:
        "Vous êtes submergé·e par un sentiment de détresse. En tant que personne intersectionnelle, au carrefour de plusieurs identités, vous subissez un stress et une anxiété constants qui impactent chaque dimension de votre vie. Malgré tous vos efforts pour vous en sortir, la situation ne s'améliore pas et de nouveaux défis ne cessent d'émerger. Vous avez besoin d'un accompagnement holistique et personnalisé, qui va au-delà d'un suivi standard, avec une présence et une disponibilité accrues pour enfin surmonter cette épreuve. Exemples : dépression majeure, burn-out, troubles anxieux généralisés, anxiété sociale, TDAH, HPI, TOC…",
    },
  ],
  quote:
    "Il est possible de ne plus se sentir étranger dans sa propre maison, mais pour cela il nous faut prendre le temps de se rencontrer, de se pardonner et de ne plus se perdre.",
  servicesHeading:
    "Cette formule propose un total de 3 heures d'accompagnement par mois, réparties en plusieurs services :",
  services: [
    "Thérapie intersectionnelle : une séance mensuelle dédiée.",
    "Suivi continu : un appel téléphonique hebdomadaire pour un soutien régulier.",
    "Outils personnalisés : élaboration de ressources thérapeutiques adaptées à vos besoins.",
    "Réseau et ressources : aide à la recherche de ressources externes et connexion à un réseau intersectionnel sûr et qualifié.",
  ],
  pricingHeading:
    "L'accompagnement est pensé sous forme de forfait dégressif, déclinable sur 1, 3 ou 6 mois consécutifs :",
  pricing: [
    "Engagement 1 mois : 320 €",
    "Engagement 3 mois : 290 €/mois (soit 870 € au total, incluant une réduction de 90 €)",
    "Engagement 6 mois : 275 €/mois (soit 1 650 € au total, incluant une réduction de 270 €)",
  ],
  notesHeading: "Notes :",
  notes: [
    "Le paiement est possible en un ou plusieurs versements, selon votre situation.",
    "Pour un remboursement par votre mutuelle, une facture d'honoraires vous sera fournie après chaque séance. Pensez également à vérifier auprès de votre employeur s'il peut établir un contrat de prestation de service.",
    "Les OPCO et le CPF peuvent aussi être sollicités pour ce type d'accompagnement, sous condition d'éligibilité.",
  ],
};

export const individualFormulas = [formula1, formula2, formula3];

// Section L — Tableau comparatif des tarifs individuels
// Ligne de référence utilisée pour lever l'incohérence entre le texte de la
// section J et ce tableau (voir note plus haut) : c'est CE tableau qui fait foi.
export const pricingTable = {
  caption: "Comparatif des tarifs individuels (nouveaux patients / patients déjà suivis)",
  columns: [
    "Formule 1 — Thérapie intersectionnelle (1h30)",
    "Formule 2 — Focus-Action, option 1 (45 min)",
    "Formule 2 — Focus-Action, option 2 (1h15)",
    "Formule 3 — Accompagnement intersectionnel (3h/mois)",
  ],
  rows: [
    {
      label: "Nouveaux patients — 1re séance",
      values: ["150 €", "100 €", "120 €", "1 mois : 320 €"],
    },
    {
      label: "Nouveaux patients — séances suivantes",
      values: ["130 €", "80 €", "100 €", "3 mois : 290 €/mois · 6 mois : 275 €/mois"],
    },
    {
      label: "Patients déjà suivis",
      values: ["120 € toutes les séances", "70 €", "90 €", "1 mois : 255 € · 3 mois : 230 €/mois · 6 mois : 215 €/mois"],
    },
    {
      label: "Remise patients déjà suivis",
      values: ["−10 € / −30 €", "−10 € / −20 €", "−10 € / −30 €", "plus de 20 % / 20 % / 21 %"],
    },
  ],
};

// ---------------------------------------------------------------------------
// b) Couple
// ---------------------------------------------------------------------------

// Section M
export const coupleTherapy = {
  heading: "La thérapie de couple",
  subheading: "Passez du « Je » et « Tu » au « Nous » !",
  image: {
    src: "/therapie-couple.jpg",
    alt: "Couple souriant lors d'une séance de thérapie",
  },
  intro:
    "La thérapie de couple est une forme de psychothérapie qui vise à aider deux personnes engagées dans une relation amoureuse et/ou de couple à surmonter leurs conflits et à améliorer leur communication. Contrairement à une thérapie individuelle, l'unité de soin n'est pas l'individu mais le couple.",
  roleIntro:
    "Le thérapeute est un médiateur neutre et un guide. Son rôle n'est pas de prendre parti pour l'un ou l'autre, mais de vous aider à :",
  roleItems: [
    "Rétablir le dialogue ;",
    "Identifier les schémas de conflit ;",
    "Explorer vos attentes et vos besoins respectifs ;",
    "Élaborer de nouvelles stratégies pour résoudre vos problèmes.",
  ],
  goal:
    "L'objectif final n'est pas nécessairement de « sauver » la relation, mais de vous permettre de prendre ensemble une décision éclairée, qu'il s'agisse de continuer ensemble sur des bases plus saines, ou de vous séparer de manière constructive.",
  more: [
    "En tant que thérapeute intersectionnelle, je suis aussi là pour vous aider à comprendre que vos difficultés relationnelles ne sont pas uniquement liées à votre histoire personnelle.",
    "Les défis que vous rencontrez sont souvent le résultat d'un mélange complexe de vos vécus personnels, de vos expériences familiales et des enjeux de pouvoir et de discrimination que vous rencontrez dans le monde extérieur.",
    "La thérapie de couple vous offre un espace sécurisant pour explorer ces dynamiques, vous permettant de mettre en mots les tensions qui vous affectent et de trouver des solutions concrètes pour vous aider à avancer (seuls ou ensemble) vers l'épanouissement.",
  ],
  quote:
    "Sortons de la tyrannie du trop ou du pas assez. Créons-nous ensemble une relation sur mesure, au lieu de nous modeler à celle qu'on nous impose dans ce monde.",
  highlightedQuote:
    "La thérapie de couple n'est pas une fin, c'est un nouveau départ : celui où l'on cesse de se battre l'un contre l'autre pour commencer à se battre ensemble.",
  duration: "1h30. La fréquence peut varier selon votre situation, jusqu'à un mois d'écart maximum entre les séances.",
  price: "130 €",
};

// ---------------------------------------------------------------------------
// c) Interventions de groupes
// ---------------------------------------------------------------------------

// Section N — Le groupe de parole intersectionnel
// Le texte d'origine mentionnait un démarrage en novembre 2025 : cette date
// est désormais périmée et a été retirée, remplacée par une mention neutre
// (voir point signalé en fin de mission).
export const supportGroup = {
  id: "groupe-de-parole",
  title: "Le groupe de parole intersectionnel",
  subheading: "Transformer la douleur en force collective !",
  image: {
    src: "/groupe-de-parole.jpg",
    alt: "Groupe de parole, participants assis en cercle",
  },
  intro:
    "Un groupe de parole est un espace d'échange et de soutien mutuel safe, bienveillant et confidentiel où vous pouvez partager, écouter et vous connecter avec d'autres personnes qui partagent des expériences de vie similaires. C'est un lieu coconstruit, qui se concentre sur le fond au-delà de la forme, pour dire l'indicible et où vous n'avez pas besoin d'expliquer votre existence. Pour les personnes ayant des profils intersectionnels, participer à un groupe de parole est un acte de courage, de vulnérabilité, de force et de résilience.",
  paragraph2:
    "C'est l'opportunité de briser notre isolement et de libérer par notre parole nos souffrances et nos colères, mais aussi nos fiertés et nos joies. Vous devez comprendre que vous n'êtes pas seul·e à vivre certaines douleurs, discriminations et que vos expériences sont valides.",
  paragraph3:
    "Tisser des liens forts avec des personnes qui vous ressemblent et vous soutiennent est possible et à votre rythme, au sein d'une communauté bienveillante, sans jugement ni violences implicites.",
  frameworkHeading: "Fonctionnement et cadre",
  frameworkItems: [
    "Le groupe est constitué de 6 à 8 participant·e·s maximum et reste le même tout au long de l'année.",
    "J'anime ce groupe une fois par mois, sur une durée de 2 heures (incluant un temps de pause).",
    "Chacun·e s'engage à une confidentialité absolue, au non-jugement et à une écoute active de qualité. Le but est de s'écouter les uns les autres, sans s'interrompre ni donner de conseils non sollicités.",
  ],
  closing:
    "Ce groupe est une bulle où la diversité est une force. On se rencontre d'humain à humain sur le fond, au-delà de la forme et des étiquettes.",
  formats: "Deux formats : en présentiel et en visioconférence. Démarrage d'un groupe à partir de 6 inscrit·e·s minimum.",
  nextSession: "Prochaines sessions : nous contacter",
};

// Autres interventions de groupes. La plaquette ne décrit que le groupe de
// parole : cette carte s'appuie sur les partenaires "groupes" fournis par la
// cliente (src/data/partnersContent.js) et sur l'approche décrite p. 5 et 19.
export const groupInterventions = [
  {
    id: "groupes-en-structure",
    title: "Ateliers et groupes en structure",
    subheading: "Des espaces collectifs au cœur des lieux associatifs",
    description:
      "J'anime aussi des ateliers et des temps d'échange collectifs au sein d'associations et de lieux communautaires, comme le Centre LGBTQIA+ de Marseille ou Les Minots de Noailles.",
    details: [
      "Ces interventions reprennent l'esprit du groupe de parole : un espace safe, bienveillant et confidentiel, où l'on se rencontre d'humain à humain, au-delà des étiquettes.",
      "Le format (durée, fréquence, thématique, nombre de participant·e·s) est construit avec la structure, en fonction de son public et de ses besoins.",
    ],
  },
];

// ---------------------------------------------------------------------------
// d) Organismes / Entreprises
// ---------------------------------------------------------------------------

// TODO [À COMPLÉTER] : liste des services proposés aux organismes / entreprises
// et leurs textes de présentation.
// Textes tirés de la plaquette : présentation (p. 2) et définition de
// l'approche intersectionnelle (p. 5). La plaquette ne détaille pas
// d'offre chiffrée pour les organismes : tarifs et formats sont sur devis.
export const organizationsIntro =
  "Psychosociologue de formation, consultante internationale, thérapeute et formatrice depuis plus de 10 ans, j'interviens auprès des associations, institutions et entreprises sur les questions d'identités intersectionnelles, croisant culture, genre, sexualité, neuroatypie et spiritualité.";

export const organizationServices = [
  {
    id: "organisme-formation",
    title: "Formation",
    description:
      "Des formations sur les identités intersectionnelles — culture, genre, sexualité, neuroatypie, spiritualité — à destination des professionnel·le·s et des équipes.",
    details: [
      "Formatrice depuis plus de 10 ans, je transmets les clés de l'approche intersectionnelle : une méthode qui reconnaît que l'identité d'une personne se construit à travers plusieurs dimensions sociales (genre, origine, classe, orientation sexuelle, spiritualité, handicap, etc.).",
      "L'entrecroisement de ces dimensions façonne des expériences singulières de vulnérabilité, de privilège ou d'oppression. Les comprendre permet d'accompagner chaque personne de façon plus juste, contextualisée et inclusive.",
    ],
  },
  {
    id: "organisme-conseil",
    title: "Conseil et accompagnement",
    description:
      "Consultante internationale, j'accompagne les structures dans la prise en compte des identités intersectionnelles de leurs équipes et de leurs publics.",
    details: [
      "Plutôt que de réduire l'individu à une seule appartenance (femme, migrant, musulmane, personne queer, etc.), l'approche intersectionnelle prend en compte la combinaison de ces identités et leur impact sur la santé mentale, les traumatismes et les ressources personnelles.",
      "Elle permet un accompagnement plus juste, en évitant les généralisations et en s'adaptant à la réalité complexe de chaque personne.",
    ],
  },
  {
    id: "organisme-vivre-ensemble",
    title: "Changement communautaire et vivre-ensemble interculturel",
    description:
      "J'accompagne les dynamiques de changement communautaire et de vivre-ensemble interculturel.",
    details: [
      "Mon travail s'appuie sur l'approche intersectionnelle : prendre en compte la combinaison des identités de chacun·e plutôt que de réduire les personnes à une seule appartenance.",
      "Il s'adresse aux associations, collectifs, institutions et territoires qui souhaitent faire de la diversité une force.",
    ],
  },
];

// Carrousel de photos "Organismes / Entreprises" — en attendant les vraies
// photos, on démarre avec /seance-groupe.jpg comme demandé.
export const organizationPhotos = [
  {
    id: "seance-groupe",
    src: "/seance-groupe.jpg",
    alt: "Séance d'échange en petit groupe",
  },
];

// Les partenaires (logos) sont dans src/data/partnersContent.js.

// ---------------------------------------------------------------------------
// e) Prises de parole / Éducation populaire
// ---------------------------------------------------------------------------

// Carrousel temporaire (en attendant les vrais médias)
export const publicSpeakingMedia = [
  {
    id: "groupe-de-parole",
    src: "/groupe-de-parole.jpg",
    alt: "Groupe de parole, participants assis en cercle",
  },
  {
    id: "seance-groupe",
    src: "/seance-groupe.jpg",
    alt: "Séance d'échange en petit groupe",
  },
];

// Thèmes de prise de parole — reprennent la plaquette : approche
// intersectionnelle (p. 5), rapports de pouvoir (p. 7), présentation (p. 2).
export const publicSpeakingTopics = [
  {
    title: "L'approche intersectionnelle",
    text: "Comprendre comment le genre, l'origine, la classe, l'orientation sexuelle, la spiritualité ou le handicap s'entrecroisent et façonnent des expériences singulières de vulnérabilité, de privilège ou d'oppression.",
  },
  {
    title: "Identités plurielles et santé mentale",
    text: "L'impact de la combinaison des identités sur la santé mentale, les traumatismes et les ressources personnelles — plutôt que de réduire chacun·e à une seule appartenance.",
  },
  {
    title: "Rapports de pouvoir et discriminations",
    text: "Racisme, sexisme, classisme, homophobie, validisme : comment ces rapports de pouvoir interagissent dans nos vies et dans la vision que nous avons de nous-mêmes.",
  },
  {
    title: "Vivre-ensemble interculturel",
    text: "Les dynamiques de changement communautaire et de vivre-ensemble interculturel, pour faire de la diversité une force.",
  },
];

// TODO [À COMPLÉTER] : articles et liens vers les podcasts.
export const articlesAndPodcasts = [];
